import { useEffect, useRef, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import { faCircleCheck, faXmark } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
    Box,
    Card,
    CardActionArea,
    ClickAwayListener,
    Grow,
    IconButton,
    Paper,
    Popper,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material"
import { alpha } from "@mui/material/styles"

import { isUsableColor } from "../../../../../../lib/colorLib"
import { LocalizedStrict } from "../../../../../../modules/localization/components/AppLocalized"
import { useAppLocalization } from "../../../../../../modules/localization/components/AppLocalizationProvider"
import { SearchableText } from "../../../../search/Searchable"
import type { SelectedPriceListItem } from "../../../createOrderTypes"
import { FavoriteButton } from "../product/FavoriteButton"

import type { MeetingServicePriceList, MeetingServicePriceListsItem } from "./priceListsTypes"

type StyleProps = Readonly<{
    accentColor: string | undefined
    isSelected: boolean
}>

const useStyles = makeStyles<StyleProps>()((theme, { accentColor, isSelected }) => ({
    // The select target and the content share one grid cell instead of the content living
    // inside the action area: the action buttons must stay out of that <button>, and stacking
    // them in the same cell keeps them in normal flow, so they line up with the chip on their own.
    card: {
        position: "relative",
        height: "100%",
        minHeight: "12.5rem",
        borderRadius: "1rem",
        borderColor: isSelected ? theme.palette.tailwind.blue[600] : undefined,
        display: "grid",
        gridTemplateColumns: "1fr",
        gridTemplateRows: "auto 1fr auto",
    },
    selectTarget: {
        gridColumn: 1,
        gridRow: "1 / -1",
        borderRadius: "1rem",
    },
    // every card gets a strip: a visible neutral fallback keeps the row consistent when
    // only some price lists have a colour configured
    accentStrip: {
        height: ".25rem",
        // colorCode is merchant-configured API data, which the design token rule allows
        backgroundColor: accentColor ?? theme.palette.tailwind.slate[200],
    },
    // the content sits above the select target, and lets clicks fall through to it -
    // only the action buttons take their own clicks back
    accentStripCell: {
        gridColumn: 1,
        gridRow: 1,
        pointerEvents: "none",
    },
    cardBody: {
        gridColumn: 1,
        gridRow: 2,
        padding: "1rem",
        pointerEvents: "none",
    },
    cardActions: {
        pointerEvents: "auto",
        // MUI's small icon button renders its glyph at 1.125rem, larger than the design
        "& .MuiIconButton-root": {
            fontSize: "1rem",
        },
    },
    categoryChip: {
        alignSelf: "flex-start",
        padding: ".25rem .625rem",
        borderRadius: "62.5rem",
        textTransform: "uppercase",
        letterSpacing: ".05em",
        fontWeight: 700,
        // tag is free text typed by the merchant, so it has to survive any length. It takes its
        // pointer events back from the body so a truncated tag can still be hovered for the full
        // text - and forwards its own click to the select target it no longer falls through to.
        maxWidth: "100%",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        pointerEvents: "auto",
        cursor: "pointer",
        // the chip carries the price list's own colour, same as the strip above it
        backgroundColor: isUsableColor(accentColor) ? alpha(accentColor, 0.12) : theme.palette.tailwind.slate[100],
        color: accentColor ?? theme.palette.tailwind.slate[500],
    },
    name: {
        fontWeight: 700,
        overflowWrap: "anywhere",
    },
    teaser: {
        color: theme.palette.tailwind.slate[500],
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
    },
    // always rendered so selecting a card cannot change its height; until then it is
    // only reserved space - no border, no fill, no content
    footer: {
        gridColumn: 1,
        gridRow: 3,
        minHeight: "2.75rem",
        padding: ".75rem 1rem",
        borderTop: "1px solid transparent",
        pointerEvents: "none",
    },
    footerSelected: {
        borderTopColor: theme.palette.tailwind.blue[100],
        backgroundColor: theme.palette.tailwind.blue[50],
        color: theme.palette.tailwind.blue[600],
        fontWeight: 700,
    },
    selectedIcon: {
        fontSize: "1.25rem",
    },
    infoButton: {
        padding: ".375rem",
        color: theme.palette.tailwind.slate[400],
        "&:hover": {
            background: theme.palette.tailwind.slate[100],
            color: theme.palette.tailwind.slate[600],
        },
    },
    infoButtonOpen: {
        color: theme.palette.tailwind.blue[600],
    },
    // above the dialog, which sits at 1300
    popper: {
        zIndex: 1300,
    },
    infoPanel: {
        width: "18rem",
        maxWidth: "calc(100vw - 2rem)",
        maxHeight: "15rem",
        overflowY: "auto",
        padding: "1rem",
        borderRadius: ".875rem",
    },
    infoText: {
        color: theme.palette.tailwind.slate[700],
    },
}))

type PriceListCardProps = Readonly<{
    merchant: MeetingServicePriceListsItem
    priceList: MeetingServicePriceList
    searchRegex: RegExp | undefined
    isSelected: boolean
    onSelect: (item: SelectedPriceListItem) => void
    onToggleFavorite: (merchantId: number, priceListId: number) => void
}>

export function PriceListCard({
    merchant,
    priceList,
    searchRegex,
    isSelected,
    onSelect,
    onToggleFavorite,
}: PriceListCardProps) {
    const { classes, cx } = useStyles({ accentColor: priceList.colorCode, isSelected })
    const { l10n } = useAppLocalization()
    // the button element doubles as the popper anchor and as the focus target on close
    const [infoAnchor, setInfoAnchor] = useState<HTMLElement | null>(null)
    const closeInfoButtonRef = useRef<HTMLButtonElement>(null)
    const isInfoOpen = Boolean(infoAnchor)

    useEffect(() => {
        if (isInfoOpen) closeInfoButtonRef.current?.focus()
    }, [isInfoOpen])

    const infoOverlayId = `price-list-info-${priceList.id}`
    const infoButtonLabel = l10n.getString("create-order-select-price-list-info-button-tooltip")

    const handleSelect = () => {
        onSelect({
            merchantId: merchant.id,
            merchantName: merchant.displayName,
            priceListId: priceList.id,
            priceListName: priceList.displayName,
            shopId: merchant.shop.id,
            isOnlyOnePriceList: false,
            priceListIncludeVat: priceList.includeVat,
        })
    }

    const handleCloseInfo = () => {
        infoAnchor?.focus()
        setInfoAnchor(null)
    }

    // the anchor sits outside the popper, so without this its own click would reopen the panel
    // that the click-away had just closed
    const handleClickAway = (event: MouseEvent | TouchEvent) => {
        if (infoAnchor && event.target instanceof Node && infoAnchor.contains(event.target)) return
        setInfoAnchor(null)
    }

    return (
        <Card variant="outlined" className={classes.card}>
            <CardActionArea
                onClick={handleSelect}
                aria-pressed={isSelected}
                aria-label={priceList.displayName}
                className={classes.selectTarget}
            />
            <Box className={cx(classes.accentStrip, classes.accentStripCell)} />
            <Stack gap=".75rem" className={classes.cardBody}>
                <Stack direction="row" gap=".5rem" alignItems="center" justifyContent="space-between">
                    {priceList.tag ? (
                        <Tooltip title={priceList.tag} arrow>
                            <Typography variant="caption" onClick={handleSelect} className={classes.categoryChip}>
                                {/* inside the Typography, not around it: Tooltip needs a ref-holding child */}
                                <SearchableText search={searchRegex}>{priceList.tag}</SearchableText>
                            </Typography>
                        </Tooltip>
                    ) : (
                        <span />
                    )}
                    <Stack direction="row" gap=".25rem" alignItems="center" className={classes.cardActions}>
                        {priceList.messageToCustomers && (
                            <Tooltip title={infoButtonLabel} arrow>
                                <IconButton
                                    size="small"
                                    className={cx(classes.infoButton, isInfoOpen && classes.infoButtonOpen)}
                                    onClick={(event) => setInfoAnchor(isInfoOpen ? null : event.currentTarget)}
                                    aria-expanded={isInfoOpen}
                                    aria-controls={infoOverlayId}
                                    aria-label={infoButtonLabel}
                                >
                                    <FontAwesomeIcon icon={faCircleInfo} />
                                </IconButton>
                            </Tooltip>
                        )}
                        <FavoriteButton
                            isFavorite={priceList.isFavorite}
                            placement="inline"
                            variant="star"
                            title={l10n.getString(
                                priceList.isFavorite
                                    ? "create-order-select-price-list-favorite-remove-button-tooltip"
                                    : "create-order-select-price-list-favorite-add-button-tooltip",
                            )}
                            onClick={() => onToggleFavorite(merchant.id, priceList.id)}
                        />
                    </Stack>
                </Stack>
                <Stack gap=".375rem" flexGrow={1}>
                    <SearchableText search={searchRegex}>
                        <Typography variant="subtitle1" className={classes.name}>
                            {priceList.displayName}
                        </Typography>
                    </SearchableText>
                    {priceList.messageToCustomers && (
                        <SearchableText search={searchRegex}>
                            <Typography variant="body2" className={classes.teaser}>
                                {priceList.messageToCustomers}
                            </Typography>
                        </SearchableText>
                    )}
                </Stack>
            </Stack>
            <Stack
                direction="row"
                gap=".375rem"
                alignItems="center"
                justifyContent="flex-end"
                className={cx(classes.footer, isSelected && classes.footerSelected)}
            >
                {isSelected && (
                    <>
                        <FontAwesomeIcon icon={faCircleCheck} className={classes.selectedIcon} />
                        <LocalizedStrict id="create-order-select-price-list-selected-badge">
                            <Typography variant="body2">Selected</Typography>
                        </LocalizedStrict>
                    </>
                )}
            </Stack>

            {priceList.messageToCustomers && (
                <Popper
                    open={isInfoOpen}
                    anchorEl={infoAnchor}
                    placement="bottom-end"
                    transition
                    className={classes.popper}
                    modifiers={[{ name: "offset", options: { offset: [0, 8] } }]}
                >
                    {({ TransitionProps }) => (
                        <Grow {...TransitionProps}>
                            <Paper elevation={8} className={classes.infoPanel}>
                                <ClickAwayListener onClickAway={handleClickAway}>
                                    <Stack
                                        gap=".5rem"
                                        id={infoOverlayId}
                                        role="group"
                                        aria-label={priceList.displayName}
                                        onKeyDown={(event) => {
                                            if (event.key !== "Escape") return
                                            // the popper is portalled out, but stop it anyway so a
                                            // future move back inside cannot close the whole dialog
                                            event.stopPropagation()
                                            handleCloseInfo()
                                        }}
                                    >
                                        <Stack
                                            direction="row"
                                            gap=".5rem"
                                            alignItems="flex-start"
                                            justifyContent="space-between"
                                        >
                                            <Typography variant="subtitle2">{priceList.displayName}</Typography>
                                            <IconButton
                                                ref={closeInfoButtonRef}
                                                size="small"
                                                className={classes.infoButton}
                                                onClick={handleCloseInfo}
                                                title={l10n.getString(
                                                    "create-order-select-price-list-info-close-button",
                                                )}
                                                aria-label={l10n.getString(
                                                    "create-order-select-price-list-info-close-button",
                                                )}
                                            >
                                                <FontAwesomeIcon icon={faXmark} />
                                            </IconButton>
                                        </Stack>
                                        <Typography variant="body2" className={classes.infoText}>
                                            {priceList.messageToCustomers}
                                        </Typography>
                                    </Stack>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
            )}
        </Card>
    )
}
